import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProgrammeCard } from "@/components/site/ProgrammeCard";
import { useProgrammes } from "@/hooks/useProgrammes";
import { STATUS, TYPES } from "@/lib/api";

export default function Programmes() {
  const [sp, setSp] = useSearchParams();
  const filters = { city: sp.get("city") || "", type: sp.get("type") || "", status: sp.get("status") || "", q: sp.get("q") || "" };
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data, loading } = useProgrammes(params);
  const { data: all } = useProgrammes();
  const cities = [...new Set(all.map((p) => p.city))].sort();

  const update = (k, v) => {
    const next = new URLSearchParams(sp);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setSp(next, { replace: true });
  };

  return (
    <div data-testid="programmes-page">
      <section className="bg-[#1A1716] text-white relative grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 pb-16">
          <p className="eyebrow">Catalogue</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-4">Nos programmes</h1>
          <p className="text-white/60 mt-4 max-w-xl">Résidences, villas et appartements signés Hélium Résidences.</p>
        </div>
      </section>
      <section className="sticky top-20 z-30 bg-[#FAF7F2]/90 backdrop-blur-xl border-b border-[#2F2A28]/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-4 grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto] items-center" data-testid="programmes-filter-bar">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#756D69]" />
            <Input value={filters.q} onChange={(e) => update("q", e.target.value)} placeholder="Rechercher un programme..." className="pl-9 bg-white" data-testid="filter-search-input" />
          </div>
          <Select value={filters.city || "all"} onValueChange={(v) => update("city", v)}>
            <SelectTrigger className="bg-white" data-testid="filter-city-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.type || "all"} onValueChange={(v) => update("type", v)}>
            <SelectTrigger className="bg-white" data-testid="filter-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            {[["all", "Tous"], ...Object.entries(STATUS).map(([k, s]) => [k, s.label])].map(([k, l]) => {
              const active = (filters.status || "all") === k;
              return (
                <button key={k} onClick={() => update("status", k)} data-testid={`filter-status-${k}`}
                  className={`px-3.5 py-2 rounded-full text-xs border transition-colors duration-300 ${active ? "bg-[#2F2A28] text-white border-[#2F2A28]" : "border-[#2F2A28]/15 hover:border-[#2F2A28]/40"}`}>
                  {l}
                </button>
              );
            })}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 min-h-[40vh]">
        <p className="text-sm text-[#756D69] mb-8" data-testid="programmes-count">
          {loading ? "Chargement..." : `${data.length} programme${data.length > 1 ? "s" : ""}`}
        </p>
        {!loading && data.length === 0 && (
          <p className="font-display text-3xl text-[#756D69]" data-testid="programmes-empty">Aucun programme ne correspond à votre recherche.</p>
        )}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((p, i) => <ProgrammeCard key={p.id} p={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
